import { LiveUpdate } from '@capawesome/capacitor-live-update';
import { ref } from 'vue';

declare const __APP_VERSION__: string;

const APP_MAJOR_VERSION = __APP_VERSION__.split('.')[0];

export type UpdateStage = 'downloading' | 'restarting';
export const updateState = {
  showModal: ref(false),
  updateStage: ref<UpdateStage>('downloading'),
  updateAvailable: ref(false),
  updateCompleted: ref(false)
};

export class UpdateService {
  static async checkForUpdates(): Promise<boolean> {
    if (import.meta.env.VITE_ENV === 'local') return false;
    // Local device testing: build with VITE_DISABLE_LIVE_UPDATE=true so the build you
    // installed isn't swapped for the bundle published on the channel.
    if (import.meta.env.VITE_DISABLE_LIVE_UPDATE === 'true') return false;

    try {
      const result = await LiveUpdate.sync({ channel: APP_MAJOR_VERSION });

      if (result.nextBundleId) {
        updateState.updateAvailable.value = true;
        return true;
      }

      return false;
    } catch (error) {
      console.error('Failed to check for updates:', error);
      return false;
    }
  }

  static async performUpdate(): Promise<void> {
    try {
      // Show the update modal with "downloading" status
      updateState.showModal.value = true;
      updateState.updateStage.value = 'downloading';

      // Download the update
      const result = await LiveUpdate.sync({ channel: APP_MAJOR_VERSION });

      if (result.nextBundleId) {
        // Update downloaded, show "restarting" status
        updateState.updateStage.value = 'restarting';

        // Wait for 2 seconds before reloading
        await new Promise(resolve => setTimeout(resolve, 2000));

        // Apply the update and reload the app
        updateState.updateCompleted.value = true;
        await LiveUpdate.reload();
      } else {
        // No update available, hide the modal
        updateState.showModal.value = false;
      }
    } catch (error) {
      console.error('Failed to perform update:', error);
      updateState.showModal.value = false;
    }
  }

  static async checkAndUpdate(): Promise<void> {
    const hasUpdate = await this.checkForUpdates();
    if (hasUpdate) {
      await this.performUpdate();
    }
  }
}
